import { useUserPreferences } from '../contexts/UserPreferencesContext';
import React from 'react';
import {
  Link,
  useParams } from 'react-router-dom';
import KPChart from '../components/charts/KPChart';
import { useChart } from '../contexts/ChartContext';
import AnalysisTabBar from '../components/AnalysisTabBar';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';

import axios from 'axios';
import BirthTimeRectificationWidget from '../components/charts/BirthTimeRectificationWidget';
import { formatDisplayDate } from '../utils/dateDisplay';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
} from '@mui/material';
import { LocalizedButton as Button } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const KPDisplay: React.FC = () => {
  const { ayanamshaMode, useKpForNadiKp } = useUserPreferences();
  const effectiveAyanamshaMode = useKpForNadiKp ? 'KP' : ayanamshaMode;
  const { id } = useParams<{ id: string }>();
  const { currentChart, setCurrentChart } = useChart();
  const { state: authState } = useAuth();
  const isPremium = hasPremiumSubscription(authState.user);
  const [chartData, setChartData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [rectificationLoading, setRectificationLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    document.title = 'KP Analysis - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software';
    if (id !== 'preview') {
      fetchChart();
    }
    // eslint-disable-next-line
  }, [id, effectiveAyanamshaMode, useKpForNadiKp]);

  React.useEffect(() => {
    if (id === 'preview') {
      if (currentChart) {
        setChartData(currentChart);
        setLoading(false);
      } else {
        setError('No chart data available for preview');
        setLoading(false);
      }
    }
  }, [id, currentChart]);

  React.useEffect(() => {
    if (id !== 'preview' || !currentChart?.birthDetails || currentChart.chartType === 'horary') return;
    const currentMode = currentChart.calculationData?.ayanamshaMode;
    if (currentMode === effectiveAyanamshaMode) {
      setChartData(currentChart);
      return;
    }
    let cancelled = false;
    const birth = currentChart.birthDetails;
    setLoading(true);
    axios.post(`${API_BASE_URL}/charts/calculate`, {
      ...birth,
      dateOfBirth: String(birth.dateOfBirth).slice(0, 10),
      saveChart: false,
      calculationModule: 'kp',
      ayanamshaMode: effectiveAyanamshaMode,
      useKpForNadiKp,
      chartSettings: currentChart.chartSettings,
    }).then((response) => {
      if (!cancelled) setChartData(response.data.data.chart);
    }).catch(() => {
      if (!cancelled) setError('Failed to refresh chart with the selected Ayanamsha');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, currentChart, effectiveAyanamshaMode, useKpForNadiKp]);

  const fetchChart = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/charts/${id}`, {
        params: { module: 'kp', ayanamshaMode: effectiveAyanamshaMode, useKpForNadiKp },
      });
      setChartData(response.data.data.chart);
    } catch (err: any) {
      setError('Failed to load chart');
    } finally {
      setLoading(false);
    }
  };

  const toISODateString = (date: Date) => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getCurrentRectificationBase = () => {
    const birthDate = new Date(chartData.birthDetails.dateOfBirth);
    const timeObj = chartData.birthDetails.timeOfBirth || {};
    return new Date(Date.UTC(
      birthDate.getUTCFullYear(),
      birthDate.getUTCMonth(),
      birthDate.getUTCDate(),
      timeObj.hour || 0,
      timeObj.minute || 0,
      timeObj.second || 0
    ));
  };

  const runRectificationRecalculation = async (adjustedDateTimeUtc: Date) => {
    const safeChartSettings = {
      chartStyle: chartData.chartSettings?.chartStyle || 'North',
      language: chartData.chartSettings?.language || 'Hinglish',
    };

    const payload = {
      name: chartData.birthDetails.name,
      dateOfBirth: toISODateString(adjustedDateTimeUtc),
      timeOfBirth: {
        hour: adjustedDateTimeUtc.getUTCHours(),
        minute: adjustedDateTimeUtc.getUTCMinutes(),
        second: adjustedDateTimeUtc.getUTCSeconds(),
      },
      placeOfBirth: chartData.birthDetails.placeOfBirth,
      gender: chartData.birthDetails.gender,
      saveChart: false,
      calculationModule: 'kp',
      ayanamshaMode: effectiveAyanamshaMode,
      useKpForNadiKp,
      chartSettings: safeChartSettings,
    };

    const token = authState.token || localStorage.getItem('nadiAstroToken') || '';
    const isHoraryChart = chartData?.chartType === 'horary';
    const horaryPayload = {
      name: chartData.birthDetails.name,
      question: chartData.horaryMeta?.question || '',
      seedNumber: chartData.horaryMeta?.seedNumber,
      dateOfQuery: toISODateString(adjustedDateTimeUtc),
      timeOfQuery: payload.timeOfBirth,
      placeOfQuery: chartData.birthDetails.placeOfBirth,
      saveChart: false,
      chartSettings: safeChartSettings,
    };
    const response = await axios.post(`${API_BASE_URL}/charts/${isHoraryChart ? 'horary' : 'calculate'}`, isHoraryChart ? horaryPayload : payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const nextChart = {
      ...response.data.data.chart,
      _id: chartData._id || (id !== 'preview' ? id : undefined),
      id: chartData.id,
      userId: chartData.userId,
    };
    setChartData(nextChart);
    setCurrentChart(nextChart);
  };

  const handleRectificationAdjust = async (
    unit: 'month' | 'day' | 'hour' | 'minute' | 'second',
    delta: number
  ) => {
    try {
      setRectificationLoading(true);
      const adjusted = getCurrentRectificationBase();
      if (unit === 'month') adjusted.setUTCMonth(adjusted.getUTCMonth() + delta);
      if (unit === 'day') adjusted.setUTCDate(adjusted.getUTCDate() + delta);
      if (unit === 'hour') adjusted.setUTCHours(adjusted.getUTCHours() + delta);
      if (unit === 'minute') adjusted.setUTCMinutes(adjusted.getUTCMinutes() + delta);
      if (unit === 'second') adjusted.setUTCSeconds(adjusted.getUTCSeconds() + delta);
      await runRectificationRecalculation(adjusted);
    } catch (err: any) {
      console.error('Rectification recalculation failed:', err?.response?.data?.message || err?.message || err);
    } finally {
      setRectificationLoading(false);
    }
  };

  const handleSaveRectification = async (payload: {
    dateOfBirth: string;
    timeOfBirth: { hour: number; minute: number; second: number };
  }) => {
    if (chartData?.chartType === 'horary') return;
    const token = authState.token || localStorage.getItem('nadiAstroToken') || '';
    const response = await axios.post(`${API_BASE_URL}/charts/${id}/rectify`, {
      ...payload,
      calculationModule: 'kp',
      ayanamshaMode: effectiveAyanamshaMode,
      useKpForNadiKp,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const nextChart = response.data.data.chart;
    setChartData(nextChart);
    setCurrentChart(nextChart);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!chartData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Chart not found</Alert>
      </Container>
    );
  }

  const isHoraryChart = chartData?.chartType === 'horary';

  return (
    <Container maxWidth="xl" sx={{ py: 4, bgcolor: '#fafafa', minHeight: '100vh' }}>
      <AnalysisTabBar chartId={id!} active="kp" isHoraryChart={isHoraryChart} />

      {/* <Box sx={{ mb: 3, textAlign: 'center', bgcolor: 'primary.main', p: 3, borderRadius: 2, border: '2px solid', borderColor: 'primary.dark' }}> */}
      <Box sx={{ mb: 3, bgcolor: 'primary.main', p: 3, borderRadius: 2, border: '2px solid', borderColor: 'primary.dark',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
            {chartData.birthDetails.name}
          </Typography>
          <Typography variant="body1" color="rgba(255,255,255,0.9)">
            {chartData.birthDetails.dateOfBirth
              ? formatDisplayDate(chartData.birthDetails.dateOfBirth, { timeZone: 'UTC' })
              : ''} • {chartData.birthDetails.timeOfBirth?.hour?.toString().padStart(2, '0')}:{chartData.birthDetails.timeOfBirth?.minute?.toString().padStart(2, '0')}:{chartData.birthDetails.timeOfBirth?.second?.toString().padStart(2, '0')} • {chartData.birthDetails.placeOfBirth?.city}
          </Typography>
        </Box>
        {isPremium && id && (id !== 'preview' || isHoraryChart) && (
          <BirthTimeRectificationWidget
            chartId={id}
            birthDetails={chartData.birthDetails}
            onAdjust={handleRectificationAdjust}
            adjusting={rectificationLoading}
            onSaveRectification={handleSaveRectification}
            allowSave={!isHoraryChart && id !== 'preview'}
          />
        )}
        {id && id !== 'preview' && !isPremium && !isHoraryChart && (
          <Button component={Link} to={`/chart/${id}/rectification`} variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.65)' }}>
            🔒 AUTO BTR · Premium
          </Button>
        )}
      </Box>
      <KPChart chartData={chartData} />
    </Container>
  );
};

export default KPDisplay;
